/**
 * Mobile-native Command Center — the ONLY screen the Android app shows.
 * Bottom-tab navigation (Chats / Leads / Alerts / Menu) instead of the desktop's
 * 3-column layout, built for one-thing-at-a-time phone use. Reuses the same
 * data/endpoints and building blocks as the desktop CommandCenter (MessageThread,
 * AISalesPanel, AIControlHeader, TemplateChooser) so both surfaces stay in sync
 * with zero duplicated business logic.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { useInboxSocket } from "../../lib/useInboxSocket";
import MessageThread from "../../components/MessageThread";
import AISalesPanel from "../../components/AISalesPanel";
import AIControlHeader from "../../components/AIControlHeader";
import TemplateChooser from "../../components/TemplateChooser";
import {
  convDisplayName, convDisplaySubtitle, WaWindowBadge, NewChatModal,
  CLASSIFICATION_LABEL, CLASSIFICATION_COLOR,
} from "./CommandCenter";
import { toast } from "sonner";
import {
  MessageCircle, Flame, Bell, Menu as MenuIcon, LogOut, ArrowLeft,
  Sparkles, X, UserPlus, Search, AlertTriangle, Clock, ChevronRight,
} from "lucide-react";

const TABS = [
  { key: "chats", label: "Chats", Icon: MessageCircle, category: "all" },
  { key: "leads", label: "Leads", Icon: Flame, category: "hot_leads" },
  { key: "alerts", label: "Alerts", Icon: Bell, category: "sla_breach" },
  { key: "menu", label: "Menu", Icon: MenuIcon, category: null },
];

const LEADS_CHIPS = [
  { key: "hot_leads", label: "Hot Leads" },
  { key: "follow_up_due", label: "Follow-Up Due" },
];
const ALERTS_CHIPS = [
  { key: "sla_breach", label: "SLA Breach" },
  { key: "deadline_at_risk", label: "Deadline at Risk" },
  { key: "awaiting_human", label: "Awaiting Human" },
];

export default function MobileCommandCenter() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("chats");
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [convs, setConvs] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [showInsights, setShowInsights] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [draftInjection, setDraftInjection] = useState(null);
  const injectDraft = useCallback((d) => { setDraftInjection({ ...d, _key: Date.now() }); setShowInsights(false); }, []);
  const { liveConversation, liveMessage, liveTyping, liveAIControl, sendTyping } = useInboxSocket(true);

  const switchTab = (t) => {
    setTab(t.key);
    if (t.category) setCategory(t.category);
    setShowSearch(false);
  };

  const loadConvs = useCallback(() => {
    const q = new URLSearchParams();
    if (category && category !== "all") q.set("category", category);
    if (search) q.set("search", search);
    api.get(`/admin/command-center/conversations?${q.toString()}`).then(r => setConvs(r.data || []));
  }, [category, search]);

  const loadMetrics = useCallback(() => {
    api.get("/admin/command-center/metrics").then(r => setMetrics(r.data || {}));
  }, []);

  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => { loadMetrics(); const t = setInterval(loadMetrics, 60000); return () => clearInterval(t); }, [loadMetrics]);
  useEffect(() => { if (liveConversation || liveMessage) loadConvs(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [liveConversation, liveMessage]);

  const active = useMemo(() => convs.find(c => c.id === activeId), [convs, activeId]);

  if (active) {
    return (
      <MobileConversation
        conv={active}
        onBack={() => setActiveId(null)}
        liveMessage={liveMessage} liveTyping={liveTyping} liveAIControl={liveAIControl} sendTyping={sendTyping}
        onReload={loadConvs}
        draftInjection={draftInjection}
        showInsights={showInsights} setShowInsights={setShowInsights}
        onApplyDraft={injectDraft}
      />
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-[#FEF3E2]/40" data-testid="mobile-command-center">
      {/* Header */}
      <div className="bg-[#1a3c5e] text-white px-4 pt-3 pb-3 flex items-center justify-between shrink-0">
        {showSearch ? (
          <div className="flex items-center gap-2 flex-1 bg-white/10 rounded-full px-3 py-1.5">
            <Search className="w-4 h-4 text-white/70 shrink-0" />
            <input
              autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search name / phone / message..."
              className="bg-transparent outline-none text-sm text-white placeholder-white/50 flex-1"
            />
            <button onClick={() => { setShowSearch(false); setSearch(""); }}><X className="w-4 h-4 text-white/70" /></button>
          </div>
        ) : (
          <>
            <div className="font-display font-extrabold text-lg">
              {tab === "chats" ? "Command Center" : tab === "leads" ? "Hot Leads" : tab === "alerts" ? "Alerts" : "Menu"}
            </div>
            <div className="flex items-center gap-1">
              {tab !== "menu" && (
                <button onClick={() => setShowSearch(true)} className="p-2 rounded-full hover:bg-white/10"><Search className="w-4.5 h-4.5" /></button>
              )}
              {tab === "chats" && (
                <button onClick={() => switchTab(TABS[2])} className="p-2 rounded-full hover:bg-white/10 relative">
                  <Bell className="w-4.5 h-4.5" />
                  {metrics.unread > 0 && <span className="absolute top-0.5 right-0.5 bg-[#F7941D] text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{metrics.unread > 9 ? "9+" : metrics.unread}</span>}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Sub-filter chips for Leads / Alerts */}
      {tab === "leads" && (
        <ChipRow chips={LEADS_CHIPS} active={category} onSelect={setCategory} />
      )}
      {tab === "alerts" && (
        <ChipRow chips={ALERTS_CHIPS} active={category} onSelect={setCategory} />
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {tab === "menu" ? (
          <MenuScreen user={user} logout={logout} />
        ) : (
          <ConvList convs={convs} onOpen={setActiveId} emptyLabel={tab === "chats" ? "No conversations yet." : tab === "leads" ? "No hot leads right now." : "No alerts — all clear."} />
        )}
      </div>

      {/* FAB — new chat (Chats tab only) */}
      {tab === "chats" && (
        <button onClick={() => setShowNewChat(true)} data-testid="mcc-new-chat-fab"
          className="absolute right-4 bottom-20 w-14 h-14 rounded-full bg-[#F7941D] text-white shadow-lg flex items-center justify-center active:scale-95 transition">
          <UserPlus className="w-6 h-6" />
        </button>
      )}

      {/* Bottom tab bar */}
      <nav className="grid grid-cols-4 border-t border-black/10 bg-white shrink-0" style={{ paddingBottom: "env(safe-area-inset-bottom, 8px)" }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => switchTab(t)} data-testid={`mcc-tab-${t.key}`}
            className={`flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-bold ${tab === t.key ? "text-[#F7941D]" : "text-gray-400"}`}>
            <t.Icon className="w-5 h-5" />
            {t.label}
          </button>
        ))}
      </nav>

      {showNewChat && (
        <NewChatModal
          onClose={() => setShowNewChat(false)}
          onCreated={(convId) => { setShowNewChat(false); loadConvs(); setActiveId(convId); }}
        />
      )}
    </div>
  );
}

function ChipRow({ chips, active, onSelect }) {
  return (
    <div className="flex gap-1.5 px-3 py-2 overflow-x-auto shrink-0 bg-[#FEF3E2]/40">
      {chips.map(c => (
        <button key={c.key} onClick={() => onSelect(c.key)}
          className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${active === c.key ? "bg-[#1a3c5e] text-white" : "bg-white border border-gray-200 text-gray-600"}`}>
          {c.label}
        </button>
      ))}
    </div>
  );
}

function ConvList({ convs, onOpen, emptyLabel }) {
  if (convs.length === 0) {
    return <div className="text-center py-16 text-gray-400 text-sm">{emptyLabel}</div>;
  }
  return (
    <div>
      {convs.map(c => (
        <button key={c.id} onClick={() => onOpen(c.id)} data-testid={`mcc-conv-${c.id}`}
          className="w-full text-left px-4 py-3.5 border-b border-black/5 bg-white active:bg-gray-50 flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1a3c5e] to-[#F7941D] text-white flex items-center justify-center font-bold shrink-0">
            {(convDisplayName(c) || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="font-bold text-[#1a3c5e] truncate">{convDisplayName(c)}</span>
              <span className="text-[11px] text-gray-400 shrink-0">{(c.last_message_at || "").slice(11, 16)}</span>
            </div>
            <div className="text-xs text-gray-500 truncate">{convDisplaySubtitle(c)}</div>
            <div className="text-sm text-gray-600 truncate mt-0.5">{c.last_message_preview || "—"}</div>
            <div className="flex items-center gap-1 mt-1.5 flex-wrap">
              {c.classification && <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${CLASSIFICATION_COLOR[c.classification]}`}>{CLASSIFICATION_LABEL[c.classification]}</span>}
              {c.lead?.score >= 7 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 font-bold inline-flex items-center gap-0.5"><Flame className="w-2.5 h-2.5" /> HOT</span>}
              {c.sla_breach && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-bold inline-flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" /> SLA BREACH</span>}
              {c.unread_admin > 0 && <span className="text-[10px] px-1.5 rounded-full bg-[#F7941D] text-white font-bold ml-auto">{c.unread_admin}</span>}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function MenuScreen({ user, logout }) {
  return (
    <div className="p-4 space-y-4">
      <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1a3c5e] to-[#F7941D] text-white flex items-center justify-center font-bold text-lg shrink-0">
          {(user?.name || "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="font-bold text-[#1a3c5e] truncate">{user?.name}</div>
          <div className="text-xs text-[#F7941D] uppercase tracking-wider font-bold">{user?.role}</div>
        </div>
      </div>
      <button onClick={logout} data-testid="mcc-logout"
        className="w-full bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3 text-rose-600 font-bold active:bg-gray-50">
        <LogOut className="w-5 h-5" /> Log Out
      </button>
      <div className="text-center text-[11px] text-gray-400 pt-2">HouseDesignWala™ Command Center</div>
    </div>
  );
}

function MobileConversation({ conv, onBack, liveMessage, liveTyping, liveAIControl, sendTyping, onReload, draftInjection, showInsights, setShowInsights, onApplyDraft }) {
  const [showTemplate, setShowTemplate] = useState(false);

  const header = (
    <div className="bg-[#f0f2f5]">
      <div className="px-3 py-2.5 flex items-center gap-2 border-b border-black/5">
        <button onClick={onBack} data-testid="mcc-back-btn" className="p-1.5 -ml-1 rounded-lg active:bg-black/5 text-[#1a3c5e] shrink-0"><ArrowLeft className="w-5 h-5" /></button>
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1a3c5e] to-[#F7941D] text-white flex items-center justify-center font-bold shrink-0">{(convDisplayName(conv) || "?")[0].toUpperCase()}</div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[#1a3c5e] truncate text-sm">{convDisplayName(conv)}</div>
          <div className="text-[11px] text-gray-500 truncate">{convDisplaySubtitle(conv)}</div>
        </div>
        <button onClick={() => setShowInsights(true)} data-testid="mcc-insights-btn" className="p-2 rounded-full bg-purple-100 text-purple-700 shrink-0"><Sparkles className="w-4 h-4" /></button>
      </div>
      <div className="px-3 py-1.5 flex items-center gap-1.5 flex-wrap">
        {conv.channel === "whatsapp" && <WaWindowBadge closesAt={conv.wa_window_closes_at} isOpen={conv.wa_window_open} />}
        <AIControlHeader conversationId={conv.id} onChange={onReload} liveAIControl={liveAIControl} />
        {conv.channel === "whatsapp" && (
          <button onClick={() => setShowTemplate(true)} data-testid="mcc-template-btn" className="ml-auto text-[10px] px-2.5 py-1 bg-[#25D366] text-white font-bold rounded-full">Template</button>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 flex flex-col" data-testid="mobile-conversation">
      <MessageThread
        conversationId={conv.id}
        role="admin"
        apiBase={`/admin/inbox/${conv.id}`}
        canInternalNote={true}
        header={header}
        onUnreadChange={onReload}
        liveMessage={liveMessage}
        liveTyping={liveTyping}
        sendTyping={sendTyping}
        draftInjection={draftInjection}
        onMessageSent={({ final_text, draft_context }) => {
          if (!draft_context || !draft_context.draft) return;
          api.post("/admin/ai/copilot/reply-log", {
            conversation_id: conv.id, draft: draft_context.draft, final_text,
            source: "ai_draft_approved", style: draft_context.style,
            ai_state_intent: draft_context.ai_state_intent, ai_state_confidence: draft_context.ai_state_confidence,
          }).catch(() => {});
        }}
      />

      {showTemplate && (
        <TemplateChooser conversationId={conv.id} onClose={() => setShowTemplate(false)} onSent={onReload} />
      )}

      {/* AI Insights — slide-up sheet */}
      {showInsights && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end" onClick={() => setShowInsights(false)} data-testid="mcc-insights-overlay">
          <div className="bg-[#FEF3E2]/30 w-full max-h-[85vh] overflow-y-auto rounded-t-2xl shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-center pt-2"><div className="w-10 h-1 rounded-full bg-gray-300" /></div>
            <div className="flex items-center justify-between px-4 pt-2 pb-1">
              <span className="font-bold text-[#1a3c5e] text-sm inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-purple-600" /> AI Insights</span>
              <button onClick={() => setShowInsights(false)} data-testid="mcc-insights-close"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-3">
              <AISalesPanel conv={conv} onApplyDraft={onApplyDraft} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
